/**
 * Landing v3 — the footer (user brief 2026-09-24, laid out after a
 * competitor's): a top row with "Follow us" (our four channels) and "Get the
 * app" (the two stores) as DS icon buttons, a hairline, then the four link
 * categories across the full width (the "From students to students." tagline
 * was dropped 2026-09-25).
 *
 * Every link is one the page already had: the old footer's (company, help,
 * policies, socials, contact), the feature list, the store links. Only
 * "Follow us" and the "Company" title are new copy. DS tokens
 * throughout, so it follows light and dark with the page; the socials take
 * the buttons' own tint, the store marks keep their brand colours.
 */
import { Button } from '@flashka/shared/ui';

import { InstagramIcon, LinkedinIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { prefetchAppShell } from '@/app-shell-loader';
import DialogContact from '@/components/home-v2/ContactDialog';
import { cn } from '@/lib/utils';
import {
  FEATURE_PAGES,
  localizePath,
  SECTION_PAGES,
  splitLangFromPath,
} from '@/seo/marketing-routes';
import { isLandingShellPath } from '@/shell-paths';

import { scrollToLandingSection } from './landing-chrome';
import { LANDING_FEATURES } from './landing-features';
import { STORE_GLYPH, STORE_LABEL_KEY } from './landing-get-app';
import { useOpenStore, useStoreDevice } from './landing-store';

/** Each section link's label, as a whole i18n key. */
const SECTION_LABEL_KEY: Record<(typeof SECTION_PAGES)[number]['id'], string> = {
  agent: 'landingV3.footer.agent',
};

const DISCORD_URL = 'https://discord.gg/eafSXxrB3F';
/** The address the old footer's Contacts dialog shows. */
const CONTACT_EMAIL = 'stefandjokovic@flashka.ai';

/** The DS Button's icon contract (see `Button.types.ts`). */
type IconProps = { size?: number; color?: string; className?: string };

/** TikTok's note in one colour — the path of `components/icons/tiktok.tsx`,
 *  centred in a square box so it sits like the lucide marks beside it. */
function TikTokGlyph({ size = 20, color = 'currentColor', className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="-1 0 18 18"
      fill={color}
      className={className}
      aria-hidden
      focusable="false">
      <path d="M14.8104 4.21283C13.9107 4.02038 13.096 3.54558 12.485 2.85766C11.8741 2.16973 11.4988 1.30464 11.414 0.388508V-0.00878906H8.31038V12.3083C8.30846 12.8536 8.13573 13.3846 7.81647 13.8267C7.49721 14.2687 7.04746 14.5997 6.53042 14.7729C6.01339 14.9462 5.45506 14.9531 4.93389 14.7927C4.41272 14.6323 3.95491 14.3126 3.62479 13.8786C3.28955 13.4373 3.10359 12.9007 3.09386 12.3466C3.08414 11.7925 3.25116 11.2498 3.57071 10.797C3.89026 10.3442 4.34573 10.005 4.87106 9.82852C5.39639 9.65205 5.96427 9.6475 6.49236 9.81553V6.66238C5.31475 6.49959 4.11571 6.70895 3.06295 7.26118C2.01019 7.81341 1.15636 8.68089 0.620901 9.74228C0.0854409 10.8037 -0.104872 12.0059 0.0765811 13.1808C0.258035 14.3556 0.802179 15.4444 1.6329 16.2948C2.42512 17.1054 3.44091 17.6615 4.55061 17.8922C5.66031 18.1229 6.81359 18.0177 7.86322 17.59C8.91285 17.1623 9.81122 16.4315 10.4437 15.4909C11.0761 14.5504 11.4139 13.4427 11.414 12.3092V6.01553C12.6683 6.91223 14.1721 7.39305 15.714 7.39031V4.30653C15.4103 4.3069 15.1075 4.2755 14.8104 4.21283Z" />
    </svg>
  );
}

/** Discord's mark in one colour — the path of `components/icons/discord.tsx`. */
function DiscordGlyph({ size = 20, color = 'currentColor', className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 -28.5 256 256"
      fill={color}
      className={className}
      aria-hidden
      focusable="false">
      <path d="M216.856339,16.5966031 C200.285002,8.84328665 182.566144,3.2084988 164.041564,0 C161.766523,4.11318106 159.108624,9.64549908 157.276099,14.0464379 C137.583995,11.0849896 118.072967,11.0849896 98.7430163,14.0464379 C96.9108417,9.64549908 94.1925838,4.11318106 91.8971895,0 C73.3526068,3.2084988 55.6133949,8.86399117 39.0420583,16.6376612 C5.61752293,67.146514 -3.4433191,116.400813 1.08711069,164.955721 C23.2560196,181.510915 44.7403634,191.567697 65.8621325,198.148576 C71.0772151,190.971126 75.7283628,183.341335 79.7352139,175.300261 C72.104019,172.400575 64.7949724,168.822202 57.8887866,164.667963 C59.7209612,163.310589 61.5131304,161.891452 63.2445898,160.431257 C105.36741,180.133187 151.134928,180.133187 192.754523,160.431257 C194.506336,161.891452 196.298154,163.310589 198.110326,164.667963 C191.183787,168.842556 183.854737,172.420929 176.223542,175.320965 C180.230393,183.341335 184.861538,190.991831 190.096624,198.16893 C211.238746,191.588051 232.743023,181.531619 254.911949,164.955721 C260.227747,108.668201 245.831087,59.8662432 216.856339,16.5966031 Z M85.4738752,135.09489 C72.8290281,135.09489 62.4592217,123.290155 62.4592217,108.914901 C62.4592217,94.5396472 72.607595,82.7145587 85.4738752,82.7145587 C98.3405064,82.7145587 108.709962,94.5189427 108.488529,108.914901 C108.508531,123.290155 98.3405064,135.09489 85.4738752,135.09489 Z M170.525237,135.09489 C157.88039,135.09489 147.510584,123.290155 147.510584,108.914901 C147.510584,94.5396472 157.658606,82.7145587 170.525237,82.7145587 C183.391518,82.7145587 193.761324,94.5189427 193.539891,108.914901 C193.539891,123.290155 183.391518,135.09489 170.525237,135.09489 Z" />
    </svg>
  );
}

/** Our channels, as the old footer listed them, plus Discord (its Community link). */
const SOCIALS = [
  {
    id: 'instagram',
    href: 'https://www.instagram.com/flashka_study',
    icon: InstagramIcon,
    labelKey: 'homeV2.footer.instagram',
  },
  {
    id: 'tiktok',
    href: 'https://www.tiktok.com/@flashka_ai',
    icon: TikTokGlyph,
    labelKey: 'homeV2.footer.tiktok',
  },
  {
    id: 'linkedin',
    href: 'https://www.linkedin.com/company/flashka-ai',
    icon: LinkedinIcon,
    labelKey: 'homeV2.footer.linkedin',
  },
  {
    id: 'discord',
    href: DISCORD_URL,
    icon: DiscordGlyph,
    labelKey: 'homeV2.footer.discord',
  },
] as const;

/** The small caps over each group, as the brief's footer labels them. */
const EYEBROW_CLASS =
  'text-[12px] font-bold uppercase leading-4 tracking-[0.14em] text-secondary-400';
// 36px tall rows at the same 36px pitch as before (24px of text + a 12px
// gap): the padding makes each row's gap part of its tap target. A long
// German or Swedish word breaks (hyphenated where the browser can) rather
// than push a 320px phone's page sideways.
const LINK_CLASS =
  'inline-block py-1.5 text-[16px] font-semibold leading-6 text-fg-subtle transition-colors [overflow-wrap:anywhere] hyphens-auto hover:text-secondary-800';

const openInNewTab = (href: string) => window.open(href, '_blank', 'noopener,noreferrer');

function IconRow({
  title,
  items,
  align = 'start',
}: {
  title: string;
  items: readonly {
    id: string;
    href?: string;
    icon: (p: IconProps) => ReactNode;
    labelKey: string;
    onPress?: () => void;
  }[];
  align?: 'start' | 'end';
}) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'flex flex-col gap-4',
        align === 'end' ? 'items-end' : 'items-start',
      )}>
      <p className={EYEBROW_CLASS}>{title}</p>
      <div className="flex gap-3">
        {items.map((item) => (
          <Button
            key={item.id}
            variant="secondary"
            size="xl"
            icon={item.icon}
            accessibilityLabel={t(item.labelKey)}
            onPress={item.onPress ?? (() => item.href && openInNewTab(item.href))}
          />
        ))}
      </div>
    </div>
  );
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <nav aria-label={title} className="flex flex-col gap-5">
      <p className={EYEBROW_CLASS}>{title}</p>
      <ul className="-my-1.5 flex flex-col">{children}</ul>
    </nav>
  );
}

const APP_SHELL_WARMUP = {
  onPointerEnter: prefetchAppShell,
  onTouchStart: prefetchAppShell,
  onFocus: prefetchAppShell,
};

function FooterLink({
  to,
  external = false,
  children,
}: {
  to: string;
  external?: boolean;
  children: ReactNode;
}) {
  // An in-site link keeps the reader in the page's language (/es/home → /es/blog).
  const { pathname } = useLocation();
  const href = external ? to : localizePath(to, splitLangFromPath(pathname).lang);
  return (
    <li>
      <Link
        to={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        // The next page opens at its top: an SPA navigation keeps the
        // window's scroll, which down here is the whole landing's height.
        onClick={external ? undefined : () => window.scrollTo(0, 0)}
        // The blog, help and legal pages live in the app shell, which a
        // landing visit has not downloaded: start it at the first sign of a
        // tap (hover, touch, focus) so the tap waits less on it. About is in
        // the landing shell and needs none.
        {...(external || isLandingShellPath(href) ? {} : APP_SHELL_WARMUP)}
        className={LINK_CLASS}>
        {children}
      </Link>
    </li>
  );
}

export function LandingFooter({ onJumpTo }: { onJumpTo: (index: number) => void }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { lang } = splitLangFromPath(pathname);
  const openStore = useOpenStore();
  // On a phone or tablet a store opens in place (the store app takes over).
  const onDevice = useStoreDevice() !== null;
  const stores = (['ios', 'android'] as const).map((store) => ({
    id: store,
    icon: STORE_GLYPH[store],
    labelKey: STORE_LABEL_KEY[store],
    onPress: () => openStore(store, 'footer', onDevice),
  }));
  return (
    // A plain ground under the dotted page: `background` without the dots, a
    // hairline above.
    <footer className="w-full border-t border-secondary-200 bg-background">
      {/* The floating call to action never leaves the screen (landing-get-app):
          on a device with a store, and on the phone layout, the footer ends
          with room for it, so it never covers the last links. */}
      <div
        className={cn(
          'mx-auto flex w-full max-w-[1080px] flex-col px-6 pb-32 pt-16',
          !onDevice && 'lg:pb-20',
        )}>
        <div className="flex flex-wrap items-end justify-between gap-8">
          <IconRow title={t('landingV3.footer.followUs')} items={SOCIALS} />
          <IconRow title={t('landingV3.nav.getApp')} items={stores} align="end" />
        </div>
        <div aria-hidden className="my-12 h-px w-full bg-secondary-200" />
        {/* The link categories alone (user 2026-09-25: the tagline went),
            four equal columns across the full width, each left-aligned. */}
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 md:grid-cols-4">
          <FooterColumn title={t('landingV3.footer.company')}>
            <FooterLink to="/home/about">{t('landingV3.nav.aboutUs')}</FooterLink>
            <FooterLink to="/blog">{t('homeV2.footer.blog')}</FooterLink>
            <FooterLink to={DISCORD_URL} external>
              {t('homeV2.footer.community')}
            </FooterLink>
          </FooterColumn>
          <FooterColumn title={t('landingV3.nav.studyTools')}>
            {/* Every feature, landing on its card — the top bar's Study Tools
                menu, repeated where a reader who scrolled to the end is. Each
                is a real link to the feature's own URL (what search lists
                under Flashka); a plain click stays on the page and jumps. */}
            {LANDING_FEATURES.map((feature, i) => {
              const page = FEATURE_PAGES.find((f) => f.id === feature.id);
              return (
                <li key={feature.id}>
                  <a
                    href={page ? localizePath(page.path, lang) : undefined}
                    onClick={(event) => {
                      if (
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey
                      )
                        return;
                      event.preventDefault();
                      onJumpTo(i);
                    }}
                    className={cn(LINK_CLASS, 'cursor-pointer text-start')}>
                    {t(feature.badgeKey)}
                  </a>
                </li>
              );
            })}
            {/* Ka's agent chat, a section with a URL of its own: on the
                landing a plain click scrolls to it; elsewhere (About) the
                link opens the landing there. */}
            {SECTION_PAGES.map((section) => (
              <li key={section.id}>
                <Link
                  to={localizePath(section.path, lang)}
                  onClick={(event) => {
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
                      return;
                    if (scrollToLandingSection(section.id, 'smooth'))
                      event.preventDefault();
                  }}
                  className={LINK_CLASS}>
                  {t(SECTION_LABEL_KEY[section.id])}
                </Link>
              </li>
            ))}
          </FooterColumn>
          <FooterColumn title={t('homeV2.footer.help')}>
            <FooterLink to="/help">{t('homeV2.footer.helpTutorials')}</FooterLink>
            <li className={LINK_CLASS}>
              <DialogContact email={CONTACT_EMAIL} />
            </li>
          </FooterColumn>
          <FooterColumn title={t('homeV2.footer.policies')}>
            <FooterLink to="/terms">{t('homeV2.footer.termsConditions')}</FooterLink>
            <FooterLink to="/privacy">{t('homeV2.footer.privacyPolicy')}</FooterLink>
          </FooterColumn>
        </div>
      </div>
    </footer>
  );
}
